/**
 * BFF-route client — the `api` sibling for Next route handlers (backlog D19).
 *
 * `api.*` reaches the .NET backend through the proxy (`/api/proxy/v1/*`). A
 * handful of calls do not go there at all: tenant selection, local login, the
 * session read and the branding derive/save routes are answered by Next route
 * handlers in `app/api/**`, because they read or write the server-side session
 * that the browser must never see. Those are still same-origin, cookie-session,
 * JSON calls — so they deserve the same contract instead of a hand-rolled
 * `fetch` per call site whose error handling drifts (one returned `res.ok`,
 * one threw `Error`, one parsed problem+json by hand).
 *
 * Differences from `api.*`, and only these:
 *
 * - **No base path.** The caller passes the full route path (`/api/auth/tenant`);
 *   there is no version segment to prepend and the routes are not uniform.
 * - **`silentUnauthorized`.** The auth routes answer 401 as their normal
 *   negative result — a failed login is not an expired session, and firing
 *   `auth:session-expired` at someone who is *on* the login page would bounce
 *   them mid-typing.
 *
 * A non-2xx throws {@link ApiError} with the parsed body, so a caller that
 * needs the payload of a negative outcome (the login form reads `outcome` off a
 * 401) reads `error.body` — the same shape `brandingProblemCode` already uses.
 */

import {
  ApiError,
  fetchOrTransportError,
  readJsonOrEmpty,
  throwApiError,
  type ApiCallOptions,
} from "./http";
import { withExpectedTenantHeader } from "./tenant-assertion";

/**
 * The one BFF route that is ALLOWED to disagree with the tab's tenant: it is the
 * route that changes it (P0-3). Sending an expectation here would make a switch
 * fail whenever a sibling tab had already switched — the very situation the
 * user is trying to resolve.
 */
const TENANT_SWITCH_PATH = "/api/auth/tenant";

export interface BffCallOptions extends ApiCallOptions {
  /**
   * Suppress the `auth:session-expired` event on a 401. Set it on endpoints
   * where 401 means "these credentials are wrong", not "your session is gone".
   */
  silentUnauthorized?: boolean;
}

async function bffRequest<T>(
  path: string,
  method: "GET" | "POST",
  body?: unknown,
  opts?: BffCallOptions,
): Promise<T> {
  // P0-3: these routes act on `session.activeTenant` too (the branding save
  // writes a tenant's palette), so they carry the same tab-expectation header
  // the data proxy compares — see `lib/api/tenant-assertion.ts`.
  const headers: Record<string, string> = body === undefined
    ? {}
    : { "Content-Type": "application/json" };
  if (path !== TENANT_SWITCH_PATH) {
    Object.assign(headers, withExpectedTenantHeader());
  }

  const response = await fetchOrTransportError(path, {
    method,
    credentials: "include",
    signal: opts?.signal,
    ...(Object.keys(headers).length > 0 ? { headers } : {}),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (!response.ok) {
    await throwApiError(response, { silentUnauthorized: opts?.silentUnauthorized });
  }

  return readJsonOrEmpty<T>(response);
}

export const bff = {
  get: <T>(path: string, opts?: BffCallOptions) => bffRequest<T>(path, "GET", undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: BffCallOptions) =>
    bffRequest<T>(path, "POST", body, opts),
};

/**
 * The parsed body of a failed {@link bff} call, or `null` for anything else
 * (a network error, an abort). Lets a caller treat a 401-with-a-payload as a
 * normal outcome without re-implementing `instanceof` plumbing.
 */
export function bffErrorBody<T>(error: unknown): T | null {
  if (!(error instanceof ApiError)) return null;
  return (error.body ?? null) as T | null;
}
